/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Box } from "@material-ui/core";
import { firestoreActions } from "modules/firestore";
import { liveActions } from "modules/live";
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { replaceBr, convertMapToValues } from "utility";
import { getLiveContextType } from "utility/live";

import type { SurveyQuizData } from "@spwn/types/firebase/firestore";
import type { AllChannel } from "modules/firestore";
import type { Store } from "store";

export const StreamingLiveEventQuizV2: React.FC = () => {
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const contextId = useSelector((state: Store) => state.live.contextId);
  const surveyData = useSelector(
    (state: Store) => state.live.surveyData
  ) as SurveyQuizData;

  // @ts-expect-error TS2345
  const [currentContextId, setCurrentContextId] = useState<string>(null);
  const [currentAnswerId, setCurrentAnswerId] = useState<number>(-1);

  const dispatch = useDispatch();

  const didMount = () => {
    return () => {
      dispatch(
        firestoreActions.closeChannel({
          channel: currentContextId as AllChannel,
        })
      );
    };
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(didMount, []);

  const watchContextId = () => {
    dispatch(
      liveActions.watchSurveyData.started({
        nextContextId: contextId,
        currentContextId,
      })
    );
    setCurrentContextId(contextId);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(watchContextId, [contextId]);

  const vote = (answerText: string, answerId: number) => {
    setCurrentAnswerId(Number(answerId));
    dispatch(
      liveActions.vote.started({
        answerText,
        answerId,
      })
    );
  };

  if (!surveyData) {
    return null;
  }

  /**
   * @description
   * アンケートの質問文を取得している。
   * 回答受付前などは、アンケートの質問文に「回答受付前です」と表示したいが、surveyDataの中にそれを判別するための値が無いため、ここでは質問文を表示し回答選択時に「回答受付前です」と表示するようにしている。
   */
  const { content } = surveyData;

  const answerRenderSwitcher = () => {
    const contextType = getLiveContextType(contextId);
    const answers = convertMapToValues(surveyData.answers);
    switch (contextType) {
      case "qid":
        if (surveyData.hasImgs) {
          return answerType02(surveyData);
        } else {
          return answerType01(answers);
        }
      default:
        return answerType01(answers);
    }
  };

  /**
   * @description テキストのみの回答タイプの時に使用されるコンポーネント
   */
  const answerType01 = (answers: string[]) => {
    return (
      <Box
        display={"grid"}
        gridTemplateColumns={["1fr", "1fr 1fr", "1fr"]}
        gridGap={"4px"}
        maxHeight={"224px"}
        overflow={"auto"}
      >
        {answers.map((text, index) => {
          const isSelected = index === currentAnswerId;
          return (
            <Box
              key={index}
              onClick={() => vote(text, index)}
              padding={"4px 16px"}
              color={getButtonColor({ isDarkModeEnabled, isSelected })}
              fontSize={"14px"}
              fontWeight={"600"}
              textAlign={"center"}
              border={"1px solid"}
              borderColor={isDarkModeEnabled ? "#fff" : "#000"}
              borderRadius={"6px"}
              bgcolor={getButtonBgColor({ isDarkModeEnabled, isSelected })}
              css={css`
                cursor: pointer;
              `}
            >
              {text}
            </Box>
          );
        })}
      </Box>
    );
  };

  /**
   * @description 画像付き回答タイプの時に使用されるコンポーネント
   */
  const answerType02 = (surveyData: SurveyQuizData) => {
    const classesLiveEventStyles = liveEventStyles({
      isDarkModeEnabled,
    });
    return (
      <div css={classesLiveEventStyles.quizLayout_02}>
        {Object.keys(surveyData.answers).map((key, index) => {
          const text = surveyData.answers[key];
          // @ts-expect-error TS18048
          const img = surveyData.imgs[key];
          const answerClass =
            index === currentAnswerId ? "selected_answer" : "";
          return (
            <div
              key={index}
              className={answerClass}
              // @ts-expect-error TS2345
              onClick={() => vote(text, index)}
            >
              <img src={img} alt="" />
              <p>{replaceBr(text)}</p>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Box display={"flex"} flexDirection={"column"} gridGap={"8px"}>
      <Box fontSize={"14px"}>{content}</Box>
      {answerRenderSwitcher()}
    </Box>
  );
};

const getButtonColor = ({
  isDarkModeEnabled,
  isSelected,
}: {
  isDarkModeEnabled: boolean;
  isSelected: boolean;
}) => {
  if (isSelected) {
    return isDarkModeEnabled ? "#000" : "#fff";
  }
  return isDarkModeEnabled ? "#fff" : "#000";
};

const getButtonBgColor = ({
  isDarkModeEnabled,
  isSelected,
}: {
  isDarkModeEnabled: boolean;
  isSelected: boolean;
}) => {
  if (isSelected) {
    return isDarkModeEnabled ? "#fff" : "#000";
  }
  return "transparent";
};

type LiveEventStylesProps = {
  THEOPlayerElementHeight?: number;
  isDarkModeEnabled?: boolean;
};
const liveEventStyles = (props: LiveEventStylesProps) => {
  const actionAreaHeight = props.THEOPlayerElementHeight
    ? css`
        height: calc(${props.THEOPlayerElementHeight}px - 46px);
      `
    : null;
  return {
    root: css`
      height: 280px;
      padding: 5%;
      border-top: 1px solid #e0e0e0;
      overflow-y: scroll;
      @media screen and (min-width: 768px) {
        ${actionAreaHeight}
        min-height: 415px;
        border: 1px solid #e0e0e0;
      }
    `,
    headline: css`
      margin-bottom: 16px;
      font-size: 14px;
      text-align: center;
      font-weight: normal;
      ${props.isDarkModeEnabled
        ? css`
            color: #fff;
          `
        : null}
    `,
    quizLayout_01: css`
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      p {
        width: calc(50% - 4px);
        padding: 10px 0;
        margin-bottom: 8px;
        color: #00c2ae;
        font-size: 14px;
        text-align: center;
        letter-spacing: 1px;
        border: 1px solid #00c2ae;
        cursor: pointer;
        &.selected_answer {
          color: #fff;
          background-color: #00c2ae;
        }
      }
    `,
    quizLayout_02: css`
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      div {
        width: calc(50% - 4px);
        padding: 16px 0;
        margin-bottom: 8px;
        color: #00c2ae;
        border: 1px solid #00c2ae;
        border-radius: 8px;
        cursor: pointer;
        &.selected_answer {
          color: #fff;
          background-color: #00c2ae;
        }
        img {
          display: block;
          width: calc(100% - 64px);
          margin: 0 auto 16px;
        }
        p {
          padding: 0 16px;
          font-size: 14px;
          text-align: center;
          letter-spacing: 1px;
        }
      }
    `,
  };
};
